import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faPause,
    faBackwardStep,
    faForwardStep,
    faVolumeHigh,
    faVolumeXmark,
    faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import GradientThumb from './GradientThumb';
import Helpers from '../Helpers';
import { useAlbumArtURL } from '../hooks/useAlbumArtURL';
import playbackTracker from '../playback/PlaybackSessionTracker';

const PlayerBar = forwardRef(({ tracks, currentTrack, setCurrentTrack, onPositionUpdate, onPlayingChange }, ref) => {
    const albumArtUrl = useAlbumArtURL(currentTrack?.project);
    const audioRef = useRef(null);
    if (!audioRef.current) audioRef.current = new Audio();
    const audioElementsRef = useRef(new Map());
    const pendingResumeRef = useRef(null);
    const loadedTrackIdRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const lastReportedSecondRef = useRef(-1);

    useEffect(() => {
        if (!expanded) return;
        const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [expanded]);

    // Bubble play state up so other UI (the project-header play button) can
    // mirror the bar's icon between play and pause.
    useEffect(() => { onPlayingChange?.(isPlaying); }, [isPlaying, onPlayingChange]);

    useImperativeHandle(ref, () => ({
        togglePlay: () => {
            if (!audioRef.current || !audioRef.current.src) return;
            if (audioRef.current.paused) {
                audioRef.current.play().catch(() => {});
                setIsPlaying(true);
            } else {
                audioRef.current.pause();
                setIsPlaying(false);
            }
        },
        loadAndPause: (track, position) => {
            pendingResumeRef.current = { trackId: track.id, position: position || 0 };
            setCurrentTrack(track);
        },
        getCurrentPosition: () => audioRef.current?.currentTime || 0,
        seekTo: (seconds) => {
            if (!audioRef.current) return;
            const dur = audioRef.current.duration;
            const target = Math.max(0, Math.min(seconds || 0, Number.isFinite(dur) ? dur : seconds || 0));
            audioRef.current.currentTime = target;
            setElapsed(target);
            if (Number.isFinite(dur) && dur > 0) setProgress((target / dur) * 100);
        },
    }));

    // Cache Audio elements by track id (not URL — signed URLs rotate per session).
    useEffect(() => {
        const cache = audioElementsRef.current;
        const wantedIds = new Set((tracks || []).map((t) => t?.id).filter(Boolean));
        for (const id of cache.keys()) {
            if (wantedIds.has(id)) continue;
            const a = cache.get(id);
            if (a !== audioRef.current) {
                a.pause();
                a.src = '';
                a.load();
            }
            cache.delete(id);
        }
    }, [tracks]);

    useEffect(() => () => {
        for (const a of audioElementsRef.current.values()) {
            a.pause();
            a.src = '';
            a.load();
        }
        audioElementsRef.current.clear();
    }, []);

    useEffect(() => {
        if (!currentTrack) {
            loadedTrackIdRef.current = null;
            return;
        }
        // Parent may pass a new currentTrack object reference for the same logical
        // track (RTDB snapshots, rename, parent re-renders). Bail so we don't
        // pause-and-restart audio that's already loaded — that was clobbering
        // user pauses and seeking back to 0.
        if (loadedTrackIdRef.current === currentTrack.id && !pendingResumeRef.current) return;
        if (currentTrack.isLocked) {
            playNext();
            return;
        }
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.pause();
        }
        loadedTrackIdRef.current = currentTrack.id;
        playbackTracker.startSession(currentTrack, currentTrack.duration || 0);

        let cancelled = false;
        let next = audioElementsRef.current.get(currentTrack.id);
        if (!next) {
            next = new Audio();
            next.preload = 'auto';
            audioElementsRef.current.set(currentTrack.id, next);
        }
        audioRef.current = next;
        audioRef.current.volume = muted ? 0 : volume;

        const setSrcAndPlay = (url) => {
            if (cancelled) return;
            if (audioRef.current.src !== url) {
                audioRef.current.src = url;
                audioRef.current.load();
            }
            const pending = pendingResumeRef.current;
            if (pending && pending.trackId === currentTrack.id) {
                pendingResumeRef.current = null;
                const seek = () => {
                    audioRef.current.currentTime = pending.position;
                    setElapsed(audioRef.current.currentTime || 0);
                    const dur = audioRef.current.duration || currentTrack.duration || 0;
                    setProgress(dur > 0 ? (audioRef.current.currentTime / dur) * 100 : 0);
                };
                if (audioRef.current.readyState >= 1) {
                    seek();
                } else {
                    const onLoad = () => {
                        seek();
                        audioRef.current.removeEventListener('loadedmetadata', onLoad);
                    };
                    audioRef.current.addEventListener('loadedmetadata', onLoad);
                }
                setIsPlaying(false);
                return;
            }
            setProgress(0);
            setElapsed(0);
            setIsPlaying(true);
            audioRef.current.play().catch(() => {});
        };

        currentTrack.resolveStreamingURL().then((url) => {
            if (!url) return;
            setSrcAndPlay(url);
        }).catch((err) => {
            console.error('Failed to resolve streaming URL for track', currentTrack.id, err);
        });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTrack]);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => {
            const p = (a.currentTime / a.duration) * 100;
            setProgress(p || 0);
            setElapsed(a.currentTime || 0);
            const sec = Math.floor(a.currentTime || 0);
            if (sec !== lastReportedSecondRef.current) {
                lastReportedSecondRef.current = sec;
                onPositionUpdate?.(a.currentTime || 0);
            }
            playbackTracker.timeObserverTick(a.currentTime || 0);
        };
        const onEnd = () => {
            playbackTracker.didCompletePlayback();
            playNext();
        };
        const onPlayEvt = () => playbackTracker.resumed();
        const onPauseEvt = () => playbackTracker.paused();
        a.addEventListener('timeupdate', onTime);
        a.addEventListener('ended', onEnd);
        a.addEventListener('play', onPlayEvt);
        a.addEventListener('pause', onPauseEvt);
        return () => {
            a.removeEventListener('timeupdate', onTime);
            a.removeEventListener('ended', onEnd);
            a.removeEventListener('play', onPlayEvt);
            a.removeEventListener('pause', onPauseEvt);
        };
    }, [currentTrack]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
    }, [volume, muted]);

    function findUnlocked(start, dir) {
        let i = start;
        while (i >= 0 && i < tracks.length) {
            if (!tracks[i].isLocked) return i;
            i += dir;
        }
        return -1;
    }

    function playPrev() {
        if (!currentTrack || !tracks?.length) return;
        const idx = tracks.findIndex((t) => t.id === currentTrack.id);
        if (audioRef.current.currentTime > 3) {
            audioRef.current.currentTime = 0;
            return;
        }
        const prev = findUnlocked(idx - 1, -1);
        if (prev >= 0) setCurrentTrack(tracks[prev]);
    }

    function playNext() {
        if (!currentTrack || !tracks?.length) return;
        const idx = tracks.findIndex((t) => t.id === currentTrack.id);
        const next = findUnlocked(idx + 1, 1);
        if (next >= 0) setCurrentTrack(tracks[next]);
    }

    function toggle() {
        if (!audioRef.current) return;
        if (audioRef.current.paused) {
            audioRef.current.play();
            setIsPlaying(true);
        } else {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    }

    function onSeek(e) {
        const v = Number(e.target.value);
        const t = (audioRef.current.duration * v) / 100;
        if (Number.isFinite(t)) {
            audioRef.current.currentTime = t;
            setProgress(v);
        }
    }

    if (!currentTrack) {
        return <footer className="player-bar empty"><div className="player-bar-empty-text">Pick a track to start playback</div></footer>;
    }

    const totalDuration = currentTrack.duration || audioRef.current?.duration || 0;

    return (
        <>
        <footer className="player-bar">
            <button
                type="button"
                className="player-bar-left"
                onClick={() => setExpanded(true)}
                aria-label="Expand player"
            >
                <GradientThumb id={currentTrack.project?.id} imageUrl={albumArtUrl} className="player-bar-art" />
                <div className="player-bar-info">
                    <div className="player-bar-track">{currentTrack.name}</div>
                    <div className="player-bar-project">{currentTrack.project?.name}</div>
                </div>
            </button>

            <div className="player-bar-center">
                <span className="player-bar-time">{Helpers.formatDuration(elapsed)}</span>
                <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.1"
                    value={progress}
                    onChange={onSeek}
                    className="player-bar-seek"
                    style={{ '--progress': `${progress}%` }}
                />
                <span className="player-bar-time">{Helpers.formatDuration(totalDuration)}</span>
            </div>

            <div className="player-bar-right">
                <button className="player-icon" onClick={playPrev} title="Previous"><FontAwesomeIcon icon={faBackwardStep} /></button>
                <button className="player-play" onClick={toggle} title={isPlaying ? 'Pause' : 'Play'}>
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                </button>
                <button className="player-icon" onClick={playNext} title="Next"><FontAwesomeIcon icon={faForwardStep} /></button>
                <button className="player-icon" onClick={() => setMuted((m) => !m)} title={muted ? 'Unmute' : 'Mute'}>
                    <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolumeHigh} />
                </button>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={muted ? 0 : volume}
                    onChange={(e) => { setVolume(Number(e.target.value)); setMuted(false); }}
                    className="player-bar-vol"
                />
            </div>
        </footer>

        {expanded && (
            <div className="player-expanded" role="dialog" aria-label="Now playing">
                <div className="player-expanded-header">
                    <button
                        className="player-expanded-close"
                        onClick={() => setExpanded(false)}
                        aria-label="Minimize player"
                    >
                        <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                    <div className="player-expanded-eyebrow">NOW PLAYING</div>
                    <div className="player-expanded-spacer" />
                </div>

                <GradientThumb
                    id={currentTrack.project?.id}
                    imageUrl={albumArtUrl}
                    className="player-expanded-art"
                />

                <div className="player-expanded-info">
                    <div className="player-expanded-track">{currentTrack.name}</div>
                    <div className="player-expanded-project">{currentTrack.project?.name}</div>
                </div>

                <div className="player-expanded-scrubber">
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="0.1"
                        value={progress}
                        onChange={onSeek}
                        className="player-bar-seek expanded-seek"
                        style={{ '--progress': `${progress}%` }}
                    />
                    <div className="player-expanded-times">
                        <span>{Helpers.formatDuration(elapsed)}</span>
                        <span>{Helpers.formatDuration(totalDuration)}</span>
                    </div>
                </div>

                <div className="player-expanded-controls">
                    <button className="player-expanded-icon" onClick={playPrev} aria-label="Previous">
                        <FontAwesomeIcon icon={faBackwardStep} />
                    </button>
                    <button className="player-expanded-play" onClick={toggle} aria-label={isPlaying ? 'Pause' : 'Play'}>
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>
                    <button className="player-expanded-icon" onClick={playNext} aria-label="Next">
                        <FontAwesomeIcon icon={faForwardStep} />
                    </button>
                </div>
            </div>
        )}
        </>
    );
});

export default PlayerBar;
